import React, { useEffect, useState, memo, useMemo } from 'react';
// Remove the import for now
import {
  Box,
  Flex,
  HStack,
  Text,
  Icon,
  Spacer,
  useToast
} from '@chakra-ui/react';
import { 
  FaCoins, 
  FaUsers, 
  FaClock, 
  FaDollarSign, 
  FaWallet, 
  FaChartLine,
  FaBell, 
  FaComments
} from 'react-icons/fa';
import { useWallet } from '@solana/wallet-adapter-react';
import CustomDisconnectButton from './CustomDisconnectButton';
// Using window.history.pushState for consistent navigation
import { getTimeUntilFridayLottery } from '../utils/priceUtils';
import priceService from '../services/priceService';
import { useBalance } from '../context/BalanceContext';

// Create a memoized StatsBoxes component to prevent re-rendering
const StatsBoxes = memo(({ lotteryCountdown, solPrice, balance }) => {
  return (
    <>
      {/* MCap */}
      <Box 
        bg="rgba(255, 58, 94, 0.1)" 
        borderRadius="md" 
        px={3.5} 
        py={1.5} 
        height="32px"
      >
        <Flex align="center">
          <Icon as={FaChartLine} color="#FF3A5E" boxSize={4} mr={1.5} />
          <Text fontSize="sm" color="#FF3A5E" fontWeight="medium">MCap: 450K</Text>
        </Flex>
      </Box>
      
      {/* JINU Price */}
      <Box 
        bg="rgba(255, 199, 0, 0.1)" 
        borderRadius="md" 
        px={3.5} 
        py={1.5} 
        height="32px"
      >
        <Flex align="center">
          <Icon as={FaCoins} color="#FFC700" boxSize={4} mr={1.5} />
          <Text fontSize="sm" color="#FFC700" fontWeight="medium">JINU: 0.028</Text>
        </Flex>
      </Box>
      
      {/* Players */}
      <Box 
        bg="rgba(0, 255, 170, 0.1)" 
        borderRadius="md" 
        px={3.5} 
        py={1.5} 
        height="32px"
      >
        <Flex align="center">
          <Icon as={FaUsers} color="#00FFAA" boxSize={4} mr={1.5} />
          <Text fontSize="sm" color="#00FFAA" fontWeight="medium">Players: 1,950</Text>
        </Flex>
      </Box>
      
      {/* Lottery */}
      <Box 
        bg="rgba(0, 163, 255, 0.1)" 
        borderRadius="md" 
        px={3.5} 
        py={1.5} 
        height="32px"
      >
        <Flex align="center">
          <Icon as={FaClock} color="#00A3FF" boxSize={4} mr={1.5} />
          <Text fontSize="sm" color="#00A3FF" fontWeight="medium">
            Lottery: {lotteryCountdown.days}d {lotteryCountdown.hours}h {lotteryCountdown.minutes}m
          </Text>
        </Flex>
      </Box>
      
      {/* SOL */}
      <Box 
        bg="rgba(167, 139, 250, 0.1)" 
        borderRadius="md" 
        px={3.5} 
        py={1.5} 
        height="32px"
      >
        <Flex align="center">
          <Icon as={FaDollarSign} color="#A78BFA" boxSize={4} mr={1.5} />
          <Text fontSize="sm" color="#A78BFA" fontWeight="medium">
            SOL: ${solPrice ? solPrice.toFixed(2) : '41.12'}
          </Text>
        </Flex>
      </Box>
      
      {/* JINU Balance */}
      <Box 
        bg="rgba(255, 145, 77, 0.1)" 
        borderRadius="md" 
        px={3.5} 
        py={1.5} 
        height="32px"
      >
        <Flex align="center">
          <Icon as={FaWallet} color="#FF914D" boxSize={4} mr={1.5} />
          <Text fontSize="sm" color="#FF914D" fontWeight="medium">
            JINU: {balance?.jinu ? balance.jinu.toLocaleString(undefined, {maximumFractionDigits: 0}) : '0'}
          </Text>
        </Flex>
      </Box>
    </>
  );
});

// Define the NavbarFix component with React.memo for deep memoization
// This component will only re-render if its props change
const NavbarFixComponent = ({ onToggleChat }) => {
  const { connected } = useWallet();
  // Custom navigation function to ensure consistent behavior
  const navigateWithoutReload = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new Event('popstate'));
  };
  const toast = useToast();
  
  // Get balance from BalanceContext
  const { balance } = useBalance();
  
  // State for lottery countdown and SOL price
  const [lotteryCountdown, setLotteryCountdown] = useState({ days: 0, hours: 0, minutes: 0 });
  const [solPrice, setSolPrice] = useState(null);
  
  // Check if user has authenticated via password
  const hasPasswordAuth = () => {
    return localStorage.getItem('palmheist_password_bypass') === 'true';
  };
  
  // Prevent full page reloads by listening to popstate events
  useEffect(() => {
    const handlePopState = () => {
      // This prevents the component from unmounting during navigation
      // and keeps the navbar stable
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);
  
  // Update lottery countdown
  useEffect(() => {
    // Initial update
    setLotteryCountdown(getTimeUntilFridayLottery());
    
    // Update every minute
    const interval = setInterval(() => {
      setLotteryCountdown(getTimeUntilFridayLottery());
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Fetch SOL price
  useEffect(() => {
    const fetchSolPrice = async () => {
      try {
        const price = await priceService.getSolanaPrice();
        setSolPrice(price);
      } catch (error) {
        console.error('Error fetching SOL price:', error);
        setSolPrice(41.12); // Fallback price
      }
    };
    
    fetchSolPrice();
    
    // Refresh price every 5 minutes
    const interval = setInterval(fetchSolPrice, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Handle wallet connection and disconnection
  useEffect(() => {
    // When wallet is connected, store that information
    if (connected) {
      localStorage.setItem('palmheist_wallet_connected', 'true');
    }
    // Only redirect if neither wallet is connected nor password auth exists
    else if (!hasPasswordAuth()) {
      // Small delay to allow the UI to update before redirecting
      const timer = setTimeout(() => {
        navigateWithoutReload('/');
        toast({
          title: "Authentication Required",
          description: "Please connect your wallet or use password authentication",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [connected, toast]);
  
  return (
    <Box 
      position="fixed" 
      top="0" 
      left="0" 
      right="0" 
      bg="#111319" 
      borderBottom="1px solid" 
      borderColor="#1E2029"
      zIndex="10"
      height="55px"
      display="flex"
      alignItems="center"
    >
      <Flex justify="space-between" align="center" width="100%" px={0}>
        {/* Empty space on left */}
        <Box width="10%" />
        
                {/* Stats info - moved 2 steps to the left */}
        <Flex justify="flex-end" width="80%" pr={20}>
          <HStack spacing={4} flexWrap="wrap" justify="center">
            <StatsBoxes lotteryCountdown={lotteryCountdown} solPrice={solPrice} balance={balance} />
          </HStack>
        </Flex>
        
        <Spacer />
        
        {/* Right side controls */}
        <HStack spacing={2} mr={3}>
          {/* Chat */}
          <Box 
            bg="rgba(0, 191, 255, 0.1)" 
            borderRadius="md" 
            px={4} 
            py={1.5} 
            height="32px"
            width="40px"
            cursor="pointer"
            onClick={onToggleChat}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FaComments} color="#00BFFF" boxSize={5} />
          </Box>
          
          {/* Notifications */}
          <Box 
            bg="rgba(255, 255, 255, 0.1)" 
            borderRadius="md" 
            px={4} 
            py={1.5} 
            height="32px"
            width="40px"
            cursor="pointer"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FaBell} color="#FFFFFF" boxSize={5} />
          </Box>
          
          {/* Wallet Connect/Disconnect */}
          <Box height="32px" display="flex" alignItems="center">
            <CustomDisconnectButton />
          </Box>
        </HStack>
      </Flex>
    </Box>
  );
};

// Export the component with React.memo and a custom comparison function
// This ensures the component only re-renders when necessary
export default memo(NavbarFixComponent, (prevProps, nextProps) => {
  // Always return true to prevent re-renders when switching tabs
  // Only allow re-renders if explicitly needed (not implemented here)
  return true;
});
